import { useCallback, useMemo, useState } from 'react';
import { FormRenderProps } from 'react-final-form';
import { connect } from 'react-redux';

import { getPlanErknmStatusTitleByStatus } from 'APIPackage/PlanListModule/common/getPlanErknmStatusTitleByStatus';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { PlanStatusEnum } from 'APIPackage/PlanListModule/enums/PlanStatusEnum';
import { FiltersModal } from 'CorePackage/FiltersModalModule/components/FiltersModal';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeIsNumber } from 'CorePackage/MainModule/validators/isNumber';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { getErpPlanLabel } from 'PlanListPackage/MainModule/common/mapPlanStatus';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { ISelectInputFieldProps, SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getUserProsecutorSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { getPlanListStatuses } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import {
  getUserProfileIsTest,
  getUserRoleIsProsecutorOrSuperProsecutor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IPLanListFilterWithMeta } from 'PlanListPackage/PlanListPanelModule/interfaces/IPLanListFilterWithMeta';
import { knmFilterFormBooleanSelectOptions } from 'KnmListPackage/KnmListPanelModule/constants/knmListFilterOptions';
import {
  getPlanListFilters,
  getPlanListFiltersCurrent,
  getPlanListFiltersCurrentId,
} from 'PlanListPackage/PlanListPanelModule/selectors/planListFiltersSelectors';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import {
  getDictionariesKnoOrganizationsSelectOptions,
  getPlanTypeFilterSelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';

import { mapFilterAdditionalFields } from '../common/mapFilterAdditionalFields';
import { initialPlanListAdditionalFields, isTestAdditionalField } from '../constants/initialPlanListAdditionalFields';
import { initialPlanListFilter } from '../constants/initialPlanListFilter';
import { usePlanListFiltersModal } from '../hooks/usePlanListFiltersModal';
import { IKnmPlanFilter } from '../interfaces/IKnmPlanFilter';
import { IPlanFilterExtraField } from '../interfaces/IPlanFilterExtraField';
import { IPlanListFiltersForm } from '../interfaces/IPlanListFiltersForm';
import styles from './PlanListFiltersModal.module.scss';

interface IPlanListFiltersModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IPlanListFiltersModalStoreProps {
  planStatuses: Array<ErknmPlanStatusesEnum | PlanStatusEnum>;
  planTypeOptions: ISelectOption[] | undefined;
  prosecutorOptions: IUserOrganisationSelectOption[] | undefined;
  isProsecutorOrSuperProsecutor: boolean;
  filters: IPLanListFilterWithMeta[];
  currentFilter: IKnmPlanFilter | undefined;
  activeFilterId: number | undefined;
  isFederalLaw248: boolean;
  isUserTest: boolean;
  erknmOrganisationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
}

const PlanListFiltersModalWithoutStore = ({
  visible,
  activeFilterId,
  currentFilter,
  filters,
  isFederalLaw248,
  planStatuses,
  planTypeOptions,
  prosecutorOptions,
  isProsecutorOrSuperProsecutor,
  onClose,
  isUserTest,
  erknmOrganisationsSelectOptions,
}: IPlanListFiltersModalProps & IPlanListFiltersModalStoreProps) => {
  const {
    getPlanListData,
    getPlanListFilterData,
    setPlanListFilterCurrent,
    setPlanListFilterSwitcher,
    createPlanListFilter,
    updatePlanListFilter,
    removePlanListFilter,
  } = usePlanListFiltersModal();

  const initialPlanListAdditionalFieldsForState = useMemo(() => {
    const result = [...initialPlanListAdditionalFields];

    if (isUserTest && !result.find(i => i.id === isTestAdditionalField.id)) {
      result.push(isTestAdditionalField);
    }

    return result;
  }, [isUserTest]);

  const [additionalFields, setAdditionalFields] = useState(initialPlanListAdditionalFieldsForState);
  const [formValues, setFormValues] = useState<IPlanListFiltersForm>(initialPlanListFilter);
  const initialFormValues = useMemo(() => {
    return isFederalLaw248 ? { ...formValues, statuses: formValues?.erknmStatuses } : formValues;
  }, [isFederalLaw248, formValues]);

  const onTemplateCreate = useCallback(() => {
    setFormValues({
      ...initialPlanListFilter,
      prosecutorOrganizationErvkUuids: prosecutorOptions ?? [],
      knoIds: erknmOrganisationsSelectOptions ?? [],
      types: planTypeOptions ?? [],
    });
    setAdditionalFields(initialPlanListAdditionalFieldsForState);
  }, [planTypeOptions, prosecutorOptions, erknmOrganisationsSelectOptions]);

  const mapFilter = useCallback(
    (filter: IKnmPlanFilter, filterName: string): IKnmPlanFilter => {
      const isIdSelected = additionalFields.find(f => f.id === 'id')?.isSelected;
      const isStatusesSelected = additionalFields.find(f => f.id === 'statuses')?.isSelected;
      const isOnlyDeletedSelected = additionalFields.find(f => f.id === 'isOnlyDeleted')?.isSelected;
      const isPeriodSelected = additionalFields.find(f => f.id === 'period')?.isSelected;

      return {
        ...filter,
        name: filterName,
        id: isIdSelected ? filter.id : initialPlanListFilter.id,
        statuses: !isFederalLaw248 && isStatusesSelected ? filter.statuses : initialPlanListFilter.statuses,
        erknmStatuses: isFederalLaw248 && isStatusesSelected ? filter.statuses : initialPlanListFilter.statuses,
        isOnlyDeleted: isOnlyDeletedSelected ? filter.isOnlyDeleted : initialPlanListFilter.isOnlyDeleted,
        startDate: isPeriodSelected ? filter.startDate : initialPlanListFilter.startDate,
        endDate: isPeriodSelected ? filter.endDate : initialPlanListFilter.endDate,
      };
    },
    [additionalFields, isFederalLaw248],
  );

  const removeAdditionalField = (name: IPlanFilterExtraField['id']) => {
    setFormValues(prev => {
      if (name === 'period') {
        return {
          ...prev,
          startDate: initialPlanListFilter.startDate,
          endDate: initialPlanListFilter.endDate,
        };
      }

      return {
        ...prev,
        [name]: initialPlanListFilter[name],
      };
    });
    setAdditionalFields(prev =>
      prev.map(f => {
        if (f.id === name) {
          return {
            ...f,
            isSelected: false,
          };
        }

        return f;
      }),
    );
  };

  const handleMapFilterAdditionalFields = useCallback(
    (filter: IKnmPlanFilter) => mapFilterAdditionalFields(filter, isUserTest),
    [isUserTest],
  );

  function renderFormContent({ values, form }: FormRenderProps<IPlanListFiltersForm>) {
    const makeRemoveFilterHandler = (name: IPlanFilterExtraField['id']) => () => {
      removeAdditionalField(name);
      if (name === 'period') {
        form.resetFieldState('startDate');
        form.resetFieldState('endDate');
      } else {
        form.resetFieldState(name);
      }
    };
    const handleRemovePeriod = () => {
      removeAdditionalField('period');
      form.resetFieldState('startDate');
      form.resetFieldState('endDate');
    };

    const getEndDateValue = () => form.getState().values.endDate;
    const getStartDateValue = () => form.getState().values.startDate;

    const planStatusOptions = planStatuses.map(status => {
      const planLabel = isFederalLaw248
        ? getPlanErknmStatusTitleByStatus(status as ErknmPlanStatusesEnum)
        : getErpPlanLabel(status as PlanStatusEnum);

      return { id: status, label: planLabel || '', value: status };
    });

    function validateControllingOrganizationsSelect(value: ISelectInputFieldProps['value']) {
      if (form.getState().values.ignoreKno) {
        return undefined;
      }

      return makeRequired('Поле не может быть пустым')(value);
    }

    const primaryContent = (
      <>
        <Row border="bottom">
          <CheckboxField id="isOnlyMine" name="isOnlyMine" label="Только мои планы" theme="system" type="checkbox" />
        </Row>
        <Row border="bottom">
          <FieldBlock label="Прокуратура" labelFontSize="small">
            <Row>
              <FieldBlock>
                <SelectInputField
                  id="prosecutorOrganizationErvkUuids"
                  name="prosecutorOrganizationErvkUuids"
                  options={prosecutorOptions}
                  isMulti
                  validate={makeRequired('Поле не может быть пустым')}
                />
              </FieldBlock>
            </Row>
            <CheckboxField
              id="childProsecutorOrgIncluded"
              name="childProsecutorOrgIncluded"
              label="Включая подчиненные организации"
              theme="system"
              type="checkbox"
            />
          </FieldBlock>
        </Row>
        <Row border="bottom">
          <Row>
            <FieldBlock label="Наименование органа контроля" labelFontSize="small">
              <SelectInputField
                id="knoIds"
                name="knoIds"
                options={erknmOrganisationsSelectOptions}
                disabled={values.ignoreKno}
                isMulti
                validate={validateControllingOrganizationsSelect}
              />
            </FieldBlock>
          </Row>
          <CheckboxField
            id="childKnoIncluded"
            name="childKnoIncluded"
            label="Включая подчиненные организации"
            theme="system"
            type="checkbox"
          />
          {isProsecutorOrSuperProsecutor && (
            <Row border="bottom">
              <CheckboxField
                id="ignoreKno"
                name="ignoreKno"
                label="Не учитывать орган контроля"
                theme="system"
                type="checkbox"
              />
            </Row>
          )}
        </Row>
        <FieldBlock label="Тип плана" labelFontSize="small">
          <SelectInputField
            id="types"
            name="types"
            options={planTypeOptions}
            isClearable={false}
            isMulti
            validate={makeRequired('Поле не может быть пустым')}
          />
        </FieldBlock>
      </>
    );

    const secondaryContent = (
      <>
        {additionalFields
          .filter(f => f.isSelected)
          .map((field, index) => (
            <Row
              key={field.id}
              border={index < additionalFields.filter(f => f.isSelected).length - 1 ? 'bottom' : undefined}
            >
              <FieldBlock
                label={field.id !== 'isOnlyDeleted' ? field.name : ''}
                labelFontSize="small"
                type="flex-label"
                button={
                  <CloseButton
                    onClick={field.id === 'period' ? handleRemovePeriod : makeRemoveFilterHandler(field.id)}
                  />
                }
              >
                {field.id === 'id' && (
                  <TextInputField name={field.id} validate={makeIsNumber('Неверный формат строки')} />
                )}
                {field.id === 'statuses' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    options={planStatusOptions}
                    isClearable={false}
                    isMulti
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'isOnlyDeleted' && (
                  <div className={styles.FilterFormGroupTitle}>
                    <CheckboxField id={field.id} name={field.id} label={field.name} theme="system" type="checkbox" />
                    <CloseButton onClick={makeRemoveFilterHandler(field.id)} />
                  </div>
                )}
                {field.id === 'period' && (
                  <div className={styles.FilterFormDateGroup}>
                    С&nbsp;
                    <div className={styles.DateFieldWrapper}>
                      <DatePickerField
                        opensOnTop
                        id="startDate"
                        name="startDate"
                        validate={composeValidators(
                          makeRequired('Поле не может быть пустым'),
                          makeNoLaterThenValidator(
                            'Дата публикации не может быть позже даты истечения новости',
                            getEndDateValue,
                          ),
                        )}
                      />
                    </div>
                    по&nbsp;
                    <div className={styles.DateFieldWrapper}>
                      <DatePickerField
                        opensOnTop
                        id="endDate"
                        name="endDate"
                        validate={composeValidators(
                          makeRequired('Поле не может быть пустым'),
                          makeNoSoonerThenValidator(
                            'Дата истечения не может быть раньше даты публикации новости',
                            getStartDateValue,
                          ),
                        )}
                      />
                    </div>
                  </div>
                )}
                {field.id === 'isTest' && (
                  <SelectInputField
                    id="isTest"
                    name="isTest"
                    options={knmFilterFormBooleanSelectOptions}
                    isClearable={false}
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
              </FieldBlock>
            </Row>
          ))}
      </>
    );

    return {
      primaryContent,
      secondaryContent,
    };
  }

  return (
    <FiltersModal<IPlanListFiltersForm, IKnmPlanFilter>
      visible={visible}
      initialFormValues={initialFormValues}
      additionalFields={additionalFields}
      filters={filters}
      currentFilter={currentFilter}
      activeFilterId={activeFilterId}
      renderFormContent={renderFormContent}
      mapFilter={mapFilter}
      mapFilterAdditionalFields={handleMapFilterAdditionalFields}
      onClose={onClose}
      onTemplateCreate={onTemplateCreate}
      setFormValues={setFormValues}
      setAdditionalFields={setAdditionalFields}
      getData={getPlanListData}
      getFilterData={getPlanListFilterData}
      setFilterCurrent={setPlanListFilterCurrent}
      setFilterSwitcher={setPlanListFilterSwitcher}
      createFilter={createPlanListFilter}
      updateFilter={updatePlanListFilter}
      removeFilter={removePlanListFilter}
    />
  );
};

const mapStateToProps = (state: IRootState): IPlanListFiltersModalStoreProps => ({
  activeFilterId: getPlanListFiltersCurrentId(state),
  currentFilter: getPlanListFiltersCurrent(state),
  filters: getPlanListFilters(state),
  isFederalLaw248: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  planStatuses: getPlanListStatuses(state),
  planTypeOptions: getPlanTypeFilterSelectOptions(state),
  prosecutorOptions: getUserProsecutorSelectOptions(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isUserTest: getUserProfileIsTest(state),
  erknmOrganisationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
});

export const PlanListFiltersModal = connect(mapStateToProps)(PlanListFiltersModalWithoutStore);
